import { Box, Checkbox, Divider, FormControlLabel, styled } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  getAllOrganizationalUnits,
  getAllWorkspaceTeams,
  getClientContactByClientIds,
  getClientList,
} from 'wisestep-api';
import { SearchBarNew, WsButton, WsColors, WsTypoColors, WsTypography } from 'wisestep-base';

import Spinner from '../../../../components/Spinner';
import { failed, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';

import { userAccessActionApiMap } from './helper/userAccessUtil';
import { PaginatedContent, UserAccessTypes } from './types/userProfileTypes';

const PositionedBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'fixed',
  bottom: 0,
  right: 0,
  zIndex: 11,
  backgroundColor: WsColors.TertiaryShades100,
  width: '30%',
  height: 70,
  padding: '0px 20px',
}));

interface IUserAccessProviderProps {
  email: string;
  activeItem: UserAccessTypes;
  onSave?: (selectedEntities: any[]) => void;
}

const UserAccessProvider = ({ email, activeItem, onSave }: IUserAccessProviderProps) => {
  const method = useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [entities, setEntities] = useState<PaginatedContent>({ content: [], totalElements: 0 });
  const [selectedEntities, setSelectedEntities] = useState<PaginatedContent['content']>([]);
  const [clientContacts, setClientContacts] = useState<PaginatedContent['content']>([]);
  const [step, setStep] = useState<'select_client' | 'grant_access'>(
    activeItem === 'Client Contacts' ? 'select_client' : 'grant_access'
  );
  const setNotification = useNotificationStore((state) => state.setNotification);
  const content =
    step === 'grant_access' && activeItem === 'Client Contacts' ? clientContacts : entities.content;
  let filteredContent = content;

  if (activeItem !== 'Clients' && (activeItem !== 'Client Contacts' || step !== 'select_client')) {
    filteredContent = content.filter((entity) =>
      (entity.name || `${entity.firstName} ${entity.lastName}`)
        ?.toLowerCase()
        .includes(method.watch('search')?.toLowerCase() || '')
    );
  }

  const fetchEntities = useCallback(
    async (activeItem: UserAccessTypes): Promise<void> => {
      switch (activeItem) {
        case 'Teams':
          {
            const response = await getAllWorkspaceTeams();
            setEntities({
              content: response.data,
              totalElements: response.data?.length ?? 0,
            });
          }
          break;
        case 'Client Contacts':
        case 'Clients':
          {
            const response = await getClientList({
              query: method.getValues('search') || '',
              pageNo: 0,
              pageSize: 100,
              sortBy: 'created_on',
              sortOrder: 'desc',
              entityType: ['CLIENT'],
            });
            setEntities(response.data);
          }
          break;
        case 'Client Units':
        case 'Account Manager Units':
        case 'Client Contact Units':
        case 'Delivery Units':
          {
            const scopeType = {
              'Client Units': 'Clients',
              'Account Manager Units': 'Account Managers',
              'Client Contact Units': 'Client Contacts',
              'Delivery Units': 'Teams',
            };
            const response = await getAllOrganizationalUnits(scopeType[activeItem]);
            setEntities({
              content: response.data || [],
              totalElements: response.data?.length ?? 0,
            });
          }
          break;
      }
    },
    [method]
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await fetchEntities(activeItem);
      } catch (err) {
        setNotification({ message: 'Error fetching Entities', type: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [activeItem, fetchEntities, setNotification]);

  const handleCheckboxSelection = (entity: any) => {
    selectedEntities.some((t) => t.id === entity.id)
      ? setSelectedEntities(selectedEntities.filter((t) => t.id !== entity.id))
      : setSelectedEntities([...selectedEntities, entity]);
  };

  const handleSelectAll = () => {
    if (selectedEntities.length === content.length) {
      setSelectedEntities([]);
    } else {
      setSelectedEntities([...content]);
    }
  };

  const handleSearch = () => {
    if (
      activeItem === 'Clients' ||
      (activeItem === 'Client Contacts' && step === 'select_client')
    ) {
      fetchEntities(activeItem);
    }
  };

  const handleAdd = async () => {
    if (selectedEntities.length === 0) {
      setNotification({ message: `Please select at least one ${activeItem}`, type: 'warning' });
      return;
    }
    try {
      await userAccessActionApiMap['ADD'][activeItem]?.(
        selectedEntities.map((t) => t.id),
        email
      );
      setNotification(success(`Provided Access successfully`));
      onSave?.(selectedEntities);
    } catch {
      console.error('Error adding entities');
      setNotification(failed(`Error occured while providing access`));
    }
  };

  const fetchClientContacts = async () => {
    setLoading(true);
    try {
      const response = await getClientContactByClientIds({
        ids: selectedEntities.map((t) => t.id),
      });
      setStep('grant_access');
      setClientContacts(response.data || []);
      setSelectedEntities([]);
    } catch {
      setNotification(failed(`Error occured while fetching Client Contacts`));
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (selectedEntities.length === 0) {
      setNotification({ message: 'Please select at least one Client', type: 'warning' });
      return;
    }
    fetchClientContacts();
  };

  const handlePrev = () => {
    setStep('select_client');
    setSelectedEntities([]);
  };

  return (
    <FormProvider {...method}>
      <Box p={2} component="form" position="relative" onSubmit={method.handleSubmit(() => {})}>
        <Box width="60%" mb={1}>
          <SearchBarNew name="search" size="small" onSearch={handleSearch} />
        </Box>
        {loading && <Spinner posY={10} backdrop />}
        <Box display="flex" alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedEntities.length === content.length && content.length > 0}
                onChange={handleSelectAll}
              />
            }
            label=""
          />
          <WsTypography bold>Select All</WsTypography>
        </Box>
        {filteredContent.map((entity) => (
          <Box key={entity.id}>
            <Box display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedEntities.some((t) => t.id === entity.id)}
                    onChange={() => handleCheckboxSelection(entity)}
                  />
                }
                label=""
              />
              <WsTypography>{entity.name || `${entity.firstName} ${entity.lastName}`}</WsTypography>
              <Box ml={1} />
              <WsTypography bold>{entity.clientName ? `(${entity.clientName})` : ''}</WsTypography>
            </Box>
            <Divider orientation="horizontal" />
          </Box>
        ))}
        <Box mt={7} />
        <PositionedBox>
          <WsTypography color={WsTypoColors.PRIMARY} bold>
            {`${selectedEntities.length}  ${
              activeItem === 'Client Contacts' && step == 'select_client' ? 'Clients' : activeItem
            }  Selected`}
          </WsTypography>
          {step === 'select_client' ? (
            <WsButton onClick={handleNext} type="button">
              Next
            </WsButton>
          ) : (
            <>
              {activeItem === 'Client Contacts' && (
                <WsButton onClick={handlePrev} type="button">
                  Prev
                </WsButton>
              )}
              <WsButton onClick={handleAdd} type="button">
                Save
              </WsButton>
            </>
          )}
        </PositionedBox>
      </Box>
    </FormProvider>
  );
};

export default UserAccessProvider;
